/**
 * La selection ne deborde pas du message ou elle commence.
 *
 * Le texte d'un post et celui d'une reponse sont les seuls endroits de
 * l'application ou l'appui long selectionne — on veut pouvoir copier une
 * adresse, un numero, le nom d'un endroit. Mais la selection, elle, ne
 * s'arretait pas au message : en tirant vers le bas elle emportait la
 * reponse suivante, puis celle d'apres, en sautant tout ce qui n'est pas
 * selectionnable. On croyait copier une phrase, on copiait une page.
 *
 * `user-select: contain` dirait exactement cela en une ligne, mais aucun
 * WebKit ne le connait. On ramene donc la selection dans son bloc a
 * chaque fois qu'elle en sort : le geste continue, la surbrillance
 * s'arrete au bord du message, et les poignees ne vont pas plus loin.
 *
 * Le bloc est celui ou le geste a commence — l'ancre —, pas celui ou le
 * doigt se trouve : tirer vers le haut depuis une reponse doit garder
 * cette reponse, pas basculer sur le post.
 */
export function limiterLaSelection(): void {
  document.addEventListener('selectionchange', () => {
    const selection = document.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;

    /*
      L'ancre d'abord. Un glissement parti d'une zone non selectionnable
      peut la poser hors de tout bloc — on se rabat alors sur le bout que
      le doigt tient, ce qui vaut mieux que de laisser courir.
    */
    const bloc = blocDe(selection.anchorNode) ?? blocDe(selection.focusNode);
    if (!bloc) return;

    const plage = selection.getRangeAt(0);
    const bornes = document.createRange();
    bornes.selectNodeContents(bloc);

    const deborde_avant = plage.compareBoundaryPoints(Range.START_TO_START, bornes) < 0;
    const deborde_apres = plage.compareBoundaryPoints(Range.END_TO_END, bornes) > 0;
    // Deja dans les clous : ne rien toucher, sinon on rejoue sans fin.
    if (!deborde_avant && !deborde_apres) return;

    const rognee = plage.cloneRange();
    if (deborde_avant) rognee.setStart(bornes.startContainer, bornes.startOffset);
    if (deborde_apres) rognee.setEnd(bornes.endContainer, bornes.endOffset);

    selection.removeAllRanges();
    selection.addRange(rognee);
  });
}

/** Le bloc de texte selectionnable qui porte ce noeud, s'il y en a un. */
function blocDe(noeud: Node | null): HTMLElement | null {
  const element = noeud instanceof Element ? noeud : (noeud?.parentElement ?? null);
  return element?.closest<HTMLElement>('.texte-libre') ?? null;
}
